import { Directive, ElementRef, HostListener } from "@angular/core";

/** Keys that keep working in a time field besides digits */
const ALLOWED_KEYS = ["Backspace", "Delete", "Tab", "Enter", "Escape", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"];

/**
 * Digits typed into a time field as "HH:mm" while typing: "1530" shows as "15:30".
 * At most four digits; anything else is dropped.
 */
export function formatTimeDigits(value: string): string {
  const digits = (value || "").replace(/\D/g, "").slice(0, 4);
  return digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
}

/**
 * A partly typed time completed to "HH:mm": "9" -> "09:00", "15" -> "15:00", "930" -> "09:30",
 * "1530" -> "15:30". Empty stays empty. The result is not range checked, see isValidTime.
 */
export function normalizeTime(value: string): string {
  const digits = (value || "").replace(/\D/g, "").slice(0, 4);
  switch (digits.length) {
    case 0:
      return "";
    case 1:
    case 2:
      return `${digits.padStart(2, "0")}:00`;
    case 3:
      return `0${digits[0]}:${digits.slice(1)}`;
    default:
      return `${digits.slice(0, 2)}:${digits.slice(2)}`;
  }
}

/** Valid 24-hour "HH:mm" time */
export function isValidTime(value: string): boolean {
  return /^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(value || "");
}

/**
 * Time field that takes only digits and adds the colon itself: typing 1530 gives 15:30.
 * Focusing the field selects its time, so typing replaces it. When leaving the field a partly
 * typed time is completed (930 -> 09:30). Works with ngModel and
 * (change): the formatted value is passed on as a normal input/change event.
 *
 * Usage: <input matInput appTimeInput [(ngModel)]="time" (change)="apply()" />
 */
@Directive({
  selector: "input[appTimeInput]",
  standalone: true,
  host: {
    inputmode: "numeric",
    maxlength: "5",
    autocomplete: "off",
  },
})
export class TimeInputDirective {
  constructor(private el: ElementRef<HTMLInputElement>) {}

  // Select the time on focus so typing replaces it. Deferred: the click that focuses the field
  // would otherwise place the caret and undo the selection.
  @HostListener("focus")
  onFocus(): void {
    setTimeout(() => this.el.nativeElement.select());
  }

  @HostListener("keydown", ["$event"])
  onKeydown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey || ALLOWED_KEYS.includes(event.key)) {
      return;
    }
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  // Also covers pasted text: non-digits are dropped
  @HostListener("input")
  onInput(): void {
    this.setValue(formatTimeDigits(this.el.nativeElement.value), false);
  }

  @HostListener("blur")
  onBlur(): void {
    this.setValue(normalizeTime(this.el.nativeElement.value), true);
  }

  /** Write a changed value and tell ngModel (input) and listeners (change) about it */
  private setValue(value: string, emitChange: boolean): void {
    const input = this.el.nativeElement;
    if (input.value === value) {
      return;
    }
    input.value = value;
    input.dispatchEvent(new Event("input"));
    if (emitChange) {
      input.dispatchEvent(new Event("change"));
    }
  }
}
